<template>
  <div class="LKMunMehCardFormRight">
    <template v-if="curator || spectator || expert">
      <div class="LKMunMehCardFormRight__row">
        <div v-if="curator || spectator" class="LKMunMehCardFormRight__stats">
          <v-skeleton-loader
            v-if="statsLoading"
            :loading="statsLoading"
            type="paragraph"
          />
          <div v-else-if="!statsLoadingError">
            <div>Всего: <strong>{{ statistics.all_count }}</strong></div>
            <div>Не приступило: <strong>{{ statistics.not_started_count }}</strong></div>
            <div>В работе: <strong>{{ statistics.in_progress_count }}</strong></div>
            <div>Сдано: <strong>{{ statistics.submitted_count }}</strong></div>
            <div>Оценка формы завершена: <strong>{{ statistics.finished_count }}</strong></div>
            <div>Оценено экспертами 1 уровня: <strong>{{ statistics.first_level_marked_count }}</strong></div>
            <div>Оценено экспертами 2 уровня: <strong>{{ statistics.second_level_marked_count }}</strong></div>
          </div>
        </div>

        <div class="LKMunMehCardFormRight__btn-container">
          <button
            v-if="spectator || curator"
            class="LKMunMehCardFormRight__btn"
            @click="onOpenParticipantsListModal"
            v-text="'Список участников'"
          />

          <button
            v-if="expert"
            class="LKMunMehCardFormRight__btn"
            @click="onOpenExpertFormsListModal"
          >
            <span
              class="LKMunMehCardFormRight__btn-text"
              v-text="'Список форм'"
            />
            <v-icon dark size="20px" v-text="'mdi-playlist-plus'" />
          </button>

          <BTableButtonList
            v-if="isReportsButtonVisible"
            :list="reportsList"
            label="Отчеты"
            @action:click="(arg1, arg2) => onDownloadReport(arg1, arg2)"
          >
            <template #activator="{ on, attrs, loading }">
              <button
                v-bind="attrs"
                class="LKMunMehCardFormRight__btn"
                :disabled="loading"
                v-on="on"
                v-text="'Отчёты'"
              />
            </template>
          </BTableButtonList>
        </div>
      </div>
    </template>


    <template v-else-if="isViewOnly">
      <div class="LKMunMehCardFormRight__btn-container">
        <button
          class="LKMunMehCardFormRight__btn"
          @click="onGoToView"
        >
          <span
            class="LKMunMehCardFormRight__btn-text"
            v-text="'Просмотр отправленной формы'"
          />
          <v-icon dark size="16px" v-text="'mdi-eye'" />
        </button>
      </div>
    </template>
    <template v-else>
      <div class="LKMunMehCardFormRight__btn-container">
        <button
          :disabled="isDisabled"
          class="LKMunMehCardFormRight__btn"
          @click="onGoToCreate"
        >
          <span
            class="LKMunMehCardFormRight__btn-text"
            v-text="btnText"
          />
          <v-icon dark size="16px" v-text="'mdi-square-edit-outline'" />
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import BTableButtonList from '@/components/tables/BTableCommon/components/BTableButtonList.vue'

import { MunMehFormStatuses } from '@/typings/api/MunMeh'
import { LK_MUN_MEH_FORM_ROUTES as ROUTES } from '@/views/LKMunMeh/Form/utils'
import { adapterMunMeh, apiMunMeh } from '@/lib/http/munmeh'
import { createLKMunMehCuratorFormsListModal, createLKMunMehExpertFormsListModal } from '@/views/LKMunMeh/utils'
import { LK_MUN_MEH_FORM_HOST_VIEW_ROUTES } from '@/views/LKMunMeh/HostView/utils'
import { downloadLinkByGet } from '@/helpers/downloadBlob'
import { LkReportsApi } from '@/api/resources'
import { getReportsList } from '@/views/LKMunMeh/reports'
import { lkMapper } from '@/store/modules/lk'
import { createModalConfirm } from '@/helpers/createModalConfirm'
import { createBTextFieldAteModal } from '@/components/forms/BTextFieldAte/utils'

export default {
  name: 'LKMunMehCardFormRight',
  components: { BTableButtonList },
  props: {
    data: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: true,
    },
    expert: Boolean,
    curator: Boolean,
    spectator: Boolean,
    slug: String,
  },
  data: () => ({
    stats: {
      all_count: 0,
      not_started_count: 0,
      in_progress_count: 0,
      submitted_count: 0,
      finished_count: 0,
      first_level_marked_count: 0,
      second_level_marked_count: 0,
    },
    statsLoading: true,
    statsLoadingError: false,
  }),
  computed: {
    ...lkMapper.mapGetters(['profile']),
    reportsList() {
      return getReportsList(this.data.is_reg_meh)
    },
    formStatus() {
      const { data, disabled } = this
      return data.form?.status
          || (disabled ? MunMehFormStatuses.not_done : MunMehFormStatuses.not_started)
    },
    btnText() {
      switch (this.formStatus) {
        case MunMehFormStatuses.not_started:
        case MunMehFormStatuses.not_done:
          return 'Заполнить'
        case MunMehFormStatuses.in_progress:
          return 'Редактировать'
        case MunMehFormStatuses.submitted:
        case MunMehFormStatuses.done:
          return 'Сдано'
        default:
          return 'Сдано'
      }
    },
    isViewOnly() {
      const { formStatus } = this
      return ![MunMehFormStatuses.not_started, MunMehFormStatuses.in_progress].includes(formStatus)
    },
    isDisabled() {
      const { formStatus, disabled } = this
      return ![MunMehFormStatuses.not_started, MunMehFormStatuses.in_progress].includes(formStatus) || disabled
    },
    isReportsButtonVisible() {
      return this.profile.is_admin || this.profile.is_superuser || this.profile.is_federal_level
    },
    statistics() {
      const stats = {}
      if (this.statsLoading) return stats
      Object.entries(this.stats).forEach((v) => {
        stats[v[0]] = Intl.NumberFormat('ru-RU').format(v[1])
      })

      return stats
    },
  },
  watch: {
    curator: {
      handler(v) {
        if (v) this.loadStats()
      },
      immediate: true,
    },
  },
  methods: {
    async onOpenParticipantsListModal() {
      const propsData = {
        data: this.data,
        title: this.data.name,
        spectator: this.spectator,
      }
      const modal = await createLKMunMehCuratorFormsListModal(this, propsData)
      await modal.open()
    },
    async onOpenExpertFormsListModal() {
      const propsData = {
        data: this.data,
        title: this.data.name,
      }
      const modal = await createLKMunMehExpertFormsListModal(this, propsData)
      await modal.open()
    },
    async loadStats() {
      this.statsLoading = true
      try {
        const { data } = await adapterMunMeh.api.getStatistics(this.data.id)
        if (Array.isArray(data) && data.length > 0) {
          // eslint-disable-next-line prefer-destructuring
          this.stats = data[0]
        } else {
          this.stats = data
        }
      } catch (e) {
        this.statsLoadingError = true
        this.$notice.errors(e)
      } finally {
        this.statsLoading = false
      }
    },
    async onDownloadReport(report, callback) {
      const unwatch = callback?.()
      let region_id = null
      try {
        if ((this.profile.is_superuser || this.profile.is_federal_level) && report.isFilterable) {
          const model = ''
          const modal = await createBTextFieldAteModal(this, {
            model,
            parent_mode: false,
            searchless: true,
            maxLevel: 1,
          })
          const [region] = await modal.open()
          if (!region) {
            unwatch?.()
            return
          }
          region_id = region.code
        }

        const payload = { code: report.value }
        if (region_id) {
          payload.params = {
            region_id,
          }
        }
        const { id } = await adapterMunMeh.api.getMunMehReport(this.data.id, payload)

        if (report.isReturnOneFile) {
          const lkReportsApi = new LkReportsApi()
          const link = lkReportsApi.getDownloadUrl(id)
          await downloadLinkByGet(link)
        } else {
          const propsData = {
            titleText: 'Загрузка отчёта',
            mainText: 'Отчёт будет сформирован в фоновом режиме. Статус его формирования будет получен в уведомлениях системы.',
            canceless: true,
            successBtn: 'Ок',
          }

          const modal = await createModalConfirm(this, propsData)
          await modal.open().finally(() => modal.close())
        }
      } catch (e) {
        this.$notice.errors(e)
      }
      unwatch?.()
    },
    async onGoToCreate() {
      if (!this.data.form) {
        try {
          const { data } = await apiMunMeh.createForm(this.data.id)
          this.data.form = data
        } catch (e) {
          this.$notice.errors(e)
          return
        }
      }

      const location = {
        name: ROUTES.FORM,
        params: {
          munMehId: this.data.id,
          formId: this.data.form.id,
          slug: this.slug,
        },
      }

      await this.$router.push(location)
    },
    async onGoToView() {
      const location = {
        name: LK_MUN_MEH_FORM_HOST_VIEW_ROUTES.FORM,
        params: {
          munMehId: this.data.id,
          formId: this.data.form.id,
          slug: this.slug,
          marksHidden: true,
        },
      }

      await this.$router.push(location)
    },
  },
}
</script>

<style lang="scss">
$right-width: 450px;

.b-ate-tree-form-recursive {
  padding-top: 0;
  padding-bottom: 0;
}

.LKMunMehCardFormRight {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: $right-width;
  // text-align: center;

  &__btn-container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__row {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &__stats {
    width: 100%;
    font-size: 12px;
    text-align: left;
    white-space: nowrap;
  }

  &__bottom-text {
    margin-top: 8px;
    font-size: 12px;
    color: $webflow-color-blue-dark-2;
    text-align: center;
  }

  &__btn {
    padding: 8px 16px;
    margin: 4px;
    font-size: 12px;
    font-weight: 600;
    color: $webflow-color-white;
    white-space: nowrap;
    background-color: $webflow-color-blue-dark;
    border-radius: 100px;
    transition: background-color $webflow-transition;

    &:hover {
      background-color: $webflow-color-blue-dark-hover;
    }

    &[disabled] {
      background-color: $webflow-color-grey;
    }

    &-text {
      margin-right: 8px;
    }
  }

  &__chart {
    display: flex;
    flex-direction: column;
    align-items: center;

    &__btn {
      @extend .LKMunMehCardFormRight__btn;

      & + & {
        margin: 4px;
      }
    }
  }
}
</style>
